"use client";

import { useState, useEffect } from "react";
import { CitizensAPI, FeaturedAPI } from "@/lib/api";
import { Citizen, FeaturedCitizen, FeaturedSection } from "@/types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CitizenSearch } from "@/components/citizens/CitizenSearch";
import { CitizenList } from "@/components/citizens/CitizenList";
import { toast } from "sonner";
import { Plus, X, GripVertical } from "lucide-react";

const sections: { value: FeaturedSection; label: string }[] = [
  { value: "hero", label: "Hero Section" },
  { value: "trending", label: "Trending" },
  { value: "footer", label: "Footer" },
  { value: "share", label: "Share" },
];

export default function FeaturedPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [featuredCitizens, setFeaturedCitizens] = useState<FeaturedCitizen[]>(
    []
  );
  const [selectedSection, setSelectedSection] = useState<
    FeaturedSection | "all"
  >("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Citizen[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [newCitizenSection, setNewCitizenSection] =
    useState<FeaturedSection>("hero");

  useEffect(() => {
    loadFeaturedCitizens();
  }, [selectedSection]);

  const loadFeaturedCitizens = async () => {
    setIsLoading(true);
    try {
      const section =
        selectedSection === "all"
          ? undefined
          : (selectedSection as FeaturedSection);
      const data = await FeaturedAPI.getAll(section);
      setFeaturedCitizens(data);
    } catch (error) {
      toast.error("Failed to load featured citizens");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const { data } = await CitizensAPI.getAll(1, 5, query);
      setSearchResults(data);
    } catch (error) {
      toast.error("Failed to search citizens");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAdd = async (citizen: Citizen) => {
    try {
      await FeaturedAPI.add(citizen.citizen_id, newCitizenSection);
      toast.success("Citizen added to featured section");
      setIsAddDialogOpen(false);
      loadFeaturedCitizens();
    } catch (error) {
      toast.error("Failed to add citizen to featured section");
    }
  };

  const handleRemove = async (featuredId: number) => {
    try {
      await FeaturedAPI.remove(featuredId);
      toast.success("Citizen removed from featured section");
      loadFeaturedCitizens();
    } catch (error) {
      toast.error("Failed to remove citizen from featured section");
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const section = source.droppableId as FeaturedSection;

    // Get citizens for this section and sort by current order
    const sectionCitizens = featuredCitizens
      .filter((fc) => fc.section === section)
      .sort((a, b) => a.order - b.order);

    // Reorder the array
    const [reorderedItem] = sectionCitizens.splice(source.index, 1);
    sectionCitizens.splice(destination.index, 0, reorderedItem);

    // Create the items array with new order numbers
    const items = sectionCitizens.map((citizen, index) => ({
      citizen_id: citizen.citizen_id,
      order: index + 1,
    }));

    console.log("Updating order for section:", section); // Debug log
    console.log("New order:", items); // Debug log

    // Optimistically update the UI
    const newFeaturedCitizens = featuredCitizens.map((fc) => {
      if (fc.section === section) {
        const newOrder = items.find(
          (item) => item.citizen_id === fc.citizen_id
        )?.order;
        return newOrder ? { ...fc, order: newOrder } : fc;
      }
      return fc;
    });
    setFeaturedCitizens(newFeaturedCitizens);

    // Update the backend
    try {
      await FeaturedAPI.updateOrder(section, items);
      toast.success("Order updated successfully");
    } catch (error) {
      console.error("Failed to update order:", error); // Debug log
      toast.error("Failed to update order");
      // Reload the original data on error
      loadFeaturedCitizens();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Featured Citizens</h1>
          <p className="text-muted-foreground">
            Manage featured citizens across different sections
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Featured Citizen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Add Featured Citizen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Section</label>
                <Select
                  value={newCitizenSection}
                  onValueChange={(value: FeaturedSection) =>
                    setNewCitizenSection(value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select section" />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map((section) => (
                      <SelectItem key={section.value} value={section.value}>
                        {section.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Search Citizens</label>
                <Input
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {isSearching ? (
                  <div className="text-center py-4">Searching...</div>
                ) : (
                  searchResults.map((citizen) => (
                    <div
                      key={citizen.citizen_id}
                      className="flex items-center justify-between p-2 border rounded-md"
                    >
                      <div className="flex items-center gap-2">
                        {citizen.profile_pic && (
                          <img
                            src={citizen.profile_pic}
                            alt={citizen.citizen_name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <span>{citizen.citizen_name}</span>
                      </div>
                      <Button size="sm" onClick={() => handleAdd(citizen)}>
                        Add
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        <Select
          value={selectedSection}
          onValueChange={(value: string) =>
            setSelectedSection(value as FeaturedSection | "all")
          }
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by section" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sections</SelectItem>
            {sections.map((section) => (
              <SelectItem key={section.value} value={section.value}>
                {section.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isLoading ? (
          <div className="text-center py-8">Loading featured citizens...</div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="space-y-4">
              {sections.map((section) => {
                const sectionCitizens = featuredCitizens
                  .filter((fc) => fc.section === section.value)
                  .sort((a, b) => a.order - b.order);

                if (
                  selectedSection !== "all" &&
                  section.value !== selectedSection
                ) {
                  return null;
                }

                return (
                  <div key={section.value} className="space-y-2">
                    <h2 className="text-xl font-semibold">{section.label}</h2>
                    <Droppable droppableId={section.value}>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="grid gap-4 grid-cols-1"
                        >
                          {sectionCitizens.map((featured, index) => (
                            <Draggable
                              key={featured.featured_id.toString()}
                              draggableId={featured.featured_id.toString()}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className="flex items-center justify-between p-4 border rounded-lg bg-background"
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      {...provided.dragHandleProps}
                                      className="cursor-grab"
                                    >
                                      <GripVertical className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    {featured.main_pic && (
                                      <img
                                        src={featured.main_pic}
                                        alt={featured.citizen_name}
                                        className="w-10 h-10 rounded-full object-cover"
                                      />
                                    )}
                                    <div>
                                      <p className="font-medium">
                                        {featured.citizen_name}
                                      </p>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {featured.tags.map((tag) => (
                                          <span
                                            key={tag.tag_id}
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary text-secondary-foreground"
                                          >
                                            {tag.tag_label}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      handleRemove(featured.featured_id)
                                    }
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        )}
      </div>
    </div>
  );
}
